import { Analytics } from './protocols/analytics'
import { AnalyticsEventParams, AnalyticsEvents } from './constants'
import { EnvironmentHelper } from '~/composables/enviromentHelper'

export class AnalyticsComposite extends Analytics {
  constructor(
    analyticsProviders = [],
    eventProviderMap = {},
    providersMap = {}
  ) {
    super()
    this.analytics = analyticsProviders
    this.eventProviderMap = eventProviderMap
    this.providersMap = providersMap
  }

  capture(eventName, properties = {}) {
    const eventValues = Object.values(AnalyticsEvents)
    if (!eventValues.includes(eventName)) {
      if (!EnvironmentHelper.isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Event "${eventName}" is not defined in AnalyticsEvents. Use AnalyticsEvents constants.`
        )
      }
    }

    const validatedParams = this.validateEventParams(eventName, properties)

    const errors = []

    const eventConfig = this.eventProviderMap[eventName]

    if (!eventConfig || !eventConfig.providers) {
      if (!EnvironmentHelper.isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Event "${eventName}" is not mapped to any provider. Add it to EventProviderMap.`
        )
      }
      return
    }

    const allowedProviders = eventConfig.providers || []
    const eventNames = eventConfig.eventNames || {}

    if (!allowedProviders || allowedProviders.length === 0) {
      if (!EnvironmentHelper.isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Event "${eventName}" is not mapped to any provider. Add it to EventProviderMap.`
        )
      }
      return
    }

    allowedProviders.forEach((providerName) => {
      const provider = this.providersMap[providerName]
      if (!provider) {
        if (!EnvironmentHelper.isProduction()) {
          // eslint-disable-next-line no-console
          console.warn(
            `[Analytics] Provider "${providerName}" not available for event "${eventName}"`
          )
        }
        return
      }

      let providerEventName = eventName
      if (eventNames[providerName]) {
        const nameMapping = eventNames[providerName]
        if (typeof nameMapping === 'function') {
          providerEventName = nameMapping(
            EnvironmentHelper.getEnviromentString(),
            validatedParams
          )
        } else {
          providerEventName = nameMapping
        }
      }

      try {
        provider.capture(providerEventName, validatedParams)
      } catch (error) {
        errors.push(error)
        if (!EnvironmentHelper.isProduction()) {
          // eslint-disable-next-line no-console
          console.error(`[Analytics Provider Error]:`, error)
        }
      }
    })

    if (errors.length > 0 && !EnvironmentHelper.isProduction()) {
      // eslint-disable-next-line no-console
      console.warn('Some analytics providers failed:', errors)
    }
  }

  validateEventParams(eventName, params = {}) {
    const expectedParams = AnalyticsEventParams[eventName]
    if (!expectedParams) {
      if (!EnvironmentHelper.isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Unknown event: ${eventName}. Proceeding without validation.`
        )
      }
      return params
    }

    const validatedParams = {}
    const expectedKeys = Object.keys(expectedParams)
    const providedKeys = Object.keys(params)

    providedKeys.forEach((key) => {
      if (expectedKeys.includes(key)) {
        validatedParams[key] = params[key]
      } else if (!EnvironmentHelper.isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Unexpected parameter "${key}" for event "${eventName}". Expected: ${expectedKeys.join(
            ', '
          )}`
        )
      }
    })

    if (!EnvironmentHelper.isProduction()) {
      const missingKeys = expectedKeys.filter(
        (key) => !providedKeys.includes(key)
      )
      if (missingKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Analytics] Missing parameters for event "${eventName}": ${missingKeys.join(
            ', '
          )}`
        )
      }
    }

    return validatedParams
  }
}
